/**
 * The /json document builder, shared by the Pages Function and the dev server.
 *
 * IMPORTANT — why this lives under `functions/` and not `src/`:
 *
 * Cloudflare Pages bundles the Functions directory as the bundler ROOT. A
 * Pages Function cannot import a module from outside that directory, so this
 * code has to be reachable from within `functions/`. That is also why the
 * technique catalog is NOT built here: catalog.json is emitted as a static
 * asset by a Vite plugin instead, which keeps /json small, keeps this module
 * dependency-free, and means the catalog is also available on a plain static
 * host such as GitHub Pages that has no Functions support at all.
 *
 * WHAT THIS DOCUMENT CAN AND CANNOT CONTAIN
 *
 * The browser half of this project (canvas, WebGL, audio, fonts, timing,
 * media codecs, entropy) cannot be reported here, because measuring it
 * requires executing JavaScript in the visitor's browser. curl and wget do not
 * execute JavaScript, so no static host can return a visitor's browser
 * fingerprint to them. Inventing one would be the most misleading thing this
 * project could do, so `browser.available` is false and `browser.reason`
 * states why.
 *
 * What IS honest here is the transport view: TLS version and cipher,
 * ClientHello length, the ClientHello cipher/extension hashes, HTTP/QUIC
 * version, TCP RTT, and the network the request arrived on. Those describe the
 * client's connection, not the browser, and they are real.
 *
 * For the browser half use the in-page Export button, or `npm run
 * export:json`, which drives a headless browser and fills this half in.
 */

export const JSON_SCHEMA = 'fingerprint-lab/1';

export interface EdgePayloadLike {
  cf: Record<string, unknown> | null;
  botManagement: Record<string, unknown> | null;
  headers: Record<string, string>;
  clientAcceptEncoding: string | null;
  colo: string | null;
  edgeConnectingIp: string | null;
  ts: number;
}

export interface TransportView {
  tlsVersion: string | null;
  tlsCipher: string | null;
  tlsClientHelloLength: number | null;
  tlsClientCiphersSha1: string | null;
  tlsClientExtensionsSha1: string | null;
  tlsClientExtensionsSha1Le: string | null;
  tlsAuth: { certPresented: boolean | null; certVerified: string | null } | null;
  httpProtocol: string | null;
  requestPriority: string | null;
  clientTcpRtt: number | null;
  clientQuicRtt: number | null;
  asn: number | null;
  asOrganization: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  timezone: string | null;
  latitude: number | null;
  longitude: number | null;
  colo: string | null;
  edgeL4DeliveryRate: number | null;
  clientAcceptEncoding: string | null;
}

function str(cf: Record<string, unknown> | null, k: string): string | null {
  if (!cf) return null;
  const v = cf[k];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function num(cf: Record<string, unknown> | null, k: string): number | null {
  if (!cf) return null;
  const v = cf[k];
  if (typeof v === 'number') return v;
  // Several cf fields arrive as numeric strings, e.g. "485".
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function bool(cf: Record<string, unknown> | null, k: string): boolean | null {
  if (!cf) return null;
  const v = cf[k];
  if (typeof v === 'boolean') return v;
  if (v === '0' || v === '1') return v === '1';
  return null;
}

/**
 * Extracts the transport view from an edge payload.
 *
 * Every field is nullable because presence on `request.cf` varies by Cloudflare
 * plan and by connection — a TLS 1.2 client has no `clientQuicRtt`, for
 * example. Missing fields are reported as null rather than omitted, so the
 * document shape stays stable for anyone diffing it.
 */
export function transportView(payload: EdgePayloadLike | null): TransportView {
  const cf = payload?.cf ?? null;
  const l4 = (cf?.['edgeL4'] as Record<string, unknown> | undefined) ?? null;
  const auth = (cf?.['tlsClientAuth'] as Record<string, unknown> | undefined) ?? null;

  return {
    tlsVersion: str(cf, 'tlsVersion'),
    tlsCipher: str(cf, 'tlsCipher'),
    tlsClientHelloLength: num(cf, 'tlsClientHelloLength'),
    tlsClientCiphersSha1: str(cf, 'tlsClientCiphersSha1'),
    tlsClientExtensionsSha1: str(cf, 'tlsClientExtensionsSha1'),
    tlsClientExtensionsSha1Le: str(cf, 'tlsClientExtensionsSha1Le'),
    tlsAuth: auth
      ? { certPresented: bool(auth, 'certPresented'), certVerified: str(auth, 'certVerified') }
      : null,
    httpProtocol: str(cf, 'httpProtocol'),
    requestPriority: str(cf, 'requestPriority') || null,
    clientTcpRtt: num(cf, 'clientTcpRtt'),
    clientQuicRtt: num(cf, 'clientQuicRtt'),
    asn: num(cf, 'asn'),
    asOrganization: str(cf, 'asOrganization'),
    country: str(cf, 'country'),
    city: str(cf, 'city'),
    region: str(cf, 'region'),
    timezone: str(cf, 'timezone'),
    latitude: num(cf, 'latitude'),
    longitude: num(cf, 'longitude'),
    colo: payload?.colo ?? str(cf, 'colo'),
    edgeL4DeliveryRate: num(l4, 'deliveryRate'),
    clientAcceptEncoding: payload?.clientAcceptEncoding ?? null,
  };
}

const BROWSER_REASON =
  "Browser-side fingerprinting (canvas, WebGL, audio, fonts, media codecs, timing, entropy) " +
  "requires executing JavaScript in the visitor's browser. curl and wget do not execute " +
  "JavaScript, so this endpoint cannot report it. Use the in-page Export button, or " +
  "`npm run export:json`, which drives a headless browser.";

export interface JsonDocument {
  schema: string;
  generatedAt: number;
  transport: TransportView;
  botManagement: {
    enabled: boolean;
    ja3Hash: string | null;
    ja4: string | null;
    score: number | null;
  };
  browser: { available: boolean; reason: string; how: string };
  catalog: { href: string };
}

export function buildJsonDocument(payload: EdgePayloadLike | null): JsonDocument {
  const bm = payload?.botManagement ?? null;
  return {
    schema: JSON_SCHEMA,
    generatedAt: Date.now(),
    transport: transportView(payload),
    botManagement: {
      // Deliberately false rather than inferred: a missing key can mean either
      // "the add-on is off" or "this field is unavailable on the current plan",
      // and conflating the two would overstate what we know.
      enabled: false,
      ja3Hash: typeof bm?.['ja3Hash'] === 'string' ? (bm['ja3Hash'] as string) : null,
      ja4: typeof bm?.['ja4'] === 'string' ? (bm['ja4'] as string) : null,
      score: typeof bm?.['score'] === 'number' ? (bm['score'] as number) : null,
    },
    browser: { available: false, reason: BROWSER_REASON, how: 'npm run export:json' },
    catalog: { href: '/catalog.json' },
  };
}
